import React from "react";
import { Flag, FileText, Download, Filter, Calendar, CheckCircle2 } from "lucide-react";
import Button from "../../components/common/Button";

const GlassCard = ({ children, className = "" }) => (
  <div className={`cht-bg-white/80 cht-backdrop-blur-xl cht-border cht-border-gray-100 cht-shadow-[0_20px_50px_rgba(0,0,0,0.04)] ${className}`}>
    {children}
  </div>
);

const AdminReports = () => {
  return (
    <div className="cht-space-y-8 cht-animate-in cht-fade-in cht-duration-700">
      <header className="cht-flex cht-flex-col md:cht-flex-row cht-items-start md:cht-items-center cht-justify-between cht-gap-4">
        <div>
          <h1 className="cht-text-3xl cht-font-black cht-text-[#1e293b] cht-tracking-tight">Reports Central</h1>
          <p className="cht-text-gray-500 cht-font-medium">Generate, schedule, and download comprehensive platform reports.</p>
        </div>
        <Button variant="primary" leftIcon={<FileText size={18} />} className="cht-rounded-2xl">
          Create Custom Report
        </Button>
      </header>

      <div className="cht-grid cht-grid-cols-1 lg:cht-grid-cols-3 cht-gap-8">
        
        {/* Report Generator Form */}
        <GlassCard className="lg:cht-col-span-1 cht-p-8 cht-rounded-[32px] cht-space-y-6">
          <div className="cht-flex cht-items-center cht-gap-3 cht-mb-6">
            <div className="cht-w-10 cht-h-10 cht-bg-blue-50 cht-text-blue-500 cht-rounded-xl cht-flex cht-items-center cht-justify-center"><Filter size={18} /></div>
            <h3 className="cht-text-lg cht-font-black cht-text-gray-900">Report Builder</h3>
          </div>
          
          <div className="cht-space-y-4">
            <div className="cht-space-y-2">
              <label className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest cht-ml-2">Data Source</label>
              <select className="cht-w-full cht-bg-gray-50 cht-border-none cht-rounded-2xl cht-p-4 cht-text-sm cht-font-bold focus:cht-ring-2 focus:cht-ring-[#3e57da]/10 transition-all">
                <option>Financials & Payouts</option>
                <option>User Acquisition & Activity</option>
                <option>Course Performance</option>
                <option>System Audits</option>
              </select>
            </div>
            
            <div className="cht-space-y-2">
              <label className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest cht-ml-2">Date Range</label>
              <div className="cht-flex cht-items-center cht-gap-2 cht-bg-gray-50 cht-rounded-2xl cht-p-2">
                <Calendar size={16} className="cht-text-gray-400 cht-ml-2" />
                <select className="cht-w-full cht-bg-transparent cht-border-none cht-p-2 cht-text-sm cht-font-bold focus:cht-ring-0">
                  <option>Last 30 Days</option>
                  <option>This Quarter</option>
                  <option>Year to Date</option>
                  <option>Custom Range</option>
                </select>
              </div>
            </div>

            <div className="cht-space-y-2">
              <label className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest cht-ml-2">Format</label>
              <div className="cht-flex cht-gap-2">
                <button className="cht-flex-1 cht-py-3 cht-bg-[#1e293b] cht-text-white cht-rounded-xl cht-text-xs cht-font-bold">PDF</button>
                <button className="cht-flex-1 cht-py-3 cht-bg-gray-50 cht-text-gray-500 hover:cht-bg-gray-100 cht-rounded-xl cht-text-xs cht-font-bold">CSV</button>
                <button className="cht-flex-1 cht-py-3 cht-bg-gray-50 cht-text-gray-500 hover:cht-bg-gray-100 cht-rounded-xl cht-text-xs cht-font-bold">JSON</button>
              </div>
            </div>
          </div>
          
          <Button variant="primary" className="cht-w-full cht-rounded-xl cht-mt-4" leftIcon={<Download size={18} />}>
            Generate Report
          </Button>
        </GlassCard>

        {/* Generated Reports List */}
        <GlassCard className="lg:cht-col-span-2 cht-p-8 cht-rounded-[32px] cht-space-y-6">
          <div className="cht-flex cht-items-center cht-gap-3 cht-mb-6">
            <div className="cht-w-10 cht-h-10 cht-bg-emerald-50 cht-text-emerald-500 cht-rounded-xl cht-flex cht-items-center cht-justify-center"><Flag size={18} /></div>
            <h3 className="cht-text-lg cht-font-black cht-text-gray-900">Recent Reports</h3>
          </div>
          
          <div className="cht-space-y-4">
            {[
              { name: "Q3 Financial Summary", date: "Oct 01, 2023", format: "PDF", size: "2.4 MB" },
              { name: "Instructor Payouts (Sept)", date: "Sep 30, 2023", format: "CSV", size: "145 KB" },
              { name: "User Growth Analysis YTD", date: "Sep 15, 2023", format: "PDF", size: "4.1 MB" },
              { name: "Course Engagement Metrics", date: "Sep 01, 2023", format: "JSON", size: "850 KB" }
            ].map((report, i) => (
              <div key={i} className="cht-flex cht-items-center cht-justify-between cht-p-4 cht-border cht-border-gray-50 hover:cht-border-gray-100 cht-rounded-2xl cht-transition-all group">
                <div className="cht-flex cht-items-center cht-gap-4">
                  <div className="cht-w-10 cht-h-10 cht-bg-gray-50 cht-rounded-lg cht-flex cht-items-center cht-justify-center cht-text-gray-400">
                    <FileText size={18} />
                  </div>
                  <div>
                    <h4 className="cht-text-sm cht-font-bold cht-text-gray-900">{report.name}</h4>
                    <div className="cht-flex cht-items-center cht-gap-2 cht-text-[10px] cht-font-bold cht-text-gray-400">
                      <span>{report.date}</span> • <span>{report.format}</span> • <span>{report.size}</span>
                    </div>
                  </div>
                </div>
                <div className="cht-flex cht-items-center cht-gap-2">
                  <span className="cht-hidden sm:cht-inline-flex cht-items-center cht-gap-1 cht-text-[9px] cht-font-black cht-uppercase cht-tracking-widest cht-text-emerald-500 cht-bg-emerald-50 cht-px-2 cht-py-1 cht-rounded-md">
                    <CheckCircle2 size={10} /> Ready
                  </span>
                  <button className="cht-w-10 cht-h-10 cht-flex cht-items-center cht-justify-center cht-bg-gray-50 hover:cht-bg-gray-100 cht-text-[#3e57da] cht-rounded-xl cht-transition-colors">
                    <Download size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </GlassCard>

      </div>
    </div>
  );
};

export default AdminReports;
